import { Button } from '@mdvip/shared/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@mdvip/shared/components/ui/tabs';
import { CreditCard, Landmark } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { BillingAddressValues, PaymentAccountFormValues, PaymentTab } from '@/lib/payments/paymentAccount.types';
import { BankAccountFields } from './BankAccountFields';
import { BillingInformationSection } from './BillingInformationSection';
import { CreditCardFields } from './CreditCardFields';

export interface PaymentMethodFormProps {
  activeTab: PaymentTab;
  onTabChange: (tab: PaymentTab) => void;
  /** Called once the active tab + billing validate; the orchestrator advances to the review screen. */
  onAdvance: () => void;
  /** Cancels the flow (the orchestrator closes + discards). */
  onCancel: () => void;
  /** When the member is already enrolled, the AutoPay checkbox is locked on. */
  autoPayEnrolled: boolean;
  savedAddress: BillingAddressValues | null;
  billingFormOpen: boolean;
  onToggleBillingForm: () => void;
}

/**
 * Screen 1 of the Add Payment Account modal: the Credit Card / Bank Account toggle, the active
 * field group, and the billing section. Consumes the shared form via context; the orchestrator
 * owns screen state and the form instance.
 */
export function PaymentMethodForm({
  activeTab,
  onTabChange,
  onAdvance,
  onCancel,
  autoPayEnrolled,
  savedAddress,
  billingFormOpen,
  onToggleBillingForm,
}: PaymentMethodFormProps) {
  const form = useFormContext<PaymentAccountFormValues>();
  const { t } = useTranslation('members');

  const handleReview = form.handleSubmit(() => onAdvance());
  const accountKind = activeTab === 'credit-card' ? 'card' : 'bank';

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={(value) => onTabChange(value as PaymentTab)}>
        <TabsList variant="line" className="w-full">
          <TabsTrigger value="credit-card">
            <CreditCard /> {t('payments.addAccount.tabs.creditCard')}
          </TabsTrigger>
          <TabsTrigger value="bank-account">
            <Landmark /> {t('payments.addAccount.tabs.bankAccount')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'credit-card' ? (
        <CreditCardFields autoPayEnrolled={autoPayEnrolled} />
      ) : (
        <BankAccountFields autoPayEnrolled={autoPayEnrolled} />
      )}

      <BillingInformationSection
        accountKind={accountKind}
        savedAddress={savedAddress}
        formOpen={billingFormOpen}
        onToggleForm={onToggleBillingForm}
      />

      <div className="flex gap-3 [&>button]:flex-1">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('payments.addAccount.cancel')}
        </Button>
        <Button type="button" onClick={handleReview}>
          {t('payments.addAccount.review')}
        </Button>
      </div>
    </div>
  );
}
